import TagManager from '../../../classes/TagManager';
import withZendesk from '../../../components/HOCs/withZendesk';
import PrimaryLoader from '../../../components/atoms/primaryLoader/PrimaryLoader';
import GiftCartPage from '../../../components/organisms/giftCardPage';
import urlHandlerConnector from '@frontastic/catwalk/src/js/app/connector/urlHandler';
import tastify from '@frontastic/catwalk/src/js/helper/tastify';
import productConnector from '@frontastic/catwalk/src/js/tastic/product/connector';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { ProductSchema } from '@srcDS/components/molecules/productSchema';
import { useCartContext } from '@srcDS/provider/cart/CartContext';
import { useNostoContext } from '@srcDS/provider/nosto/NostoContext';
import YotpoWidget from '../../../components/atoms/yotpo';
import ProductPage from '../../../components/templates/product/ProductPage';

const ProductPageTastic = ({
  node,
  data,
  product = null,
  wishlist = null,
  context,
  openChat,
  closeChat,
  isServerSideRendering,
}) => {
  const { getProductPageRecommendations, hasNostoJs, nostoUpdateCart, nostoProductClick, setPageView } =
    useNostoContext();

  // @ts-ignore
  const state = useSelector((state) => state);

  // @ts-ignore
  const hasAvailableSizeGuide = state.node.pages[state.node.currentNodeId].data.sections.main.layoutElements.find(
    (nd) => {
      return nd.tastics.some((t) => t.tasticType === 'frontastic/pages/newsizeguide');
    },
  );

  const hasGuideById = product?.categories?.some((categoryId) =>
    state?.['demo-saba']?.sizeGuideAvailableIds?.includes(categoryId),
  );

  const locale = context?.locale === 'en_AU@AUD' ? 'AU' : 'NZ';
  if (!data[`show${locale}`]) return null;

  const breadcrumbs =
    data.stream?.projectSpecificData?.categories?.map((item) => {
      return {
        ...item,
        reference: {
          target: item.url,
          type: 'link',
        },
      };
    }) || [];

  const fullBreadcrumbs = [...breadcrumbs, { name: product?.name, url: product?._url }];

  const isSSR = typeof isServerSideRendering === 'boolean' && isServerSideRendering;
  const { addToCart, addCustomLineItemToCart } = useCartContext();

  const wishlistItem =
    product &&
    wishlist?.data?.lineItems?.find((item) => product.variants.find((variant) => variant.sku === item.variant.sku));

  const isGiftPage = product?.projectSpecificData?.productType === 'gift-card-product-type';

  useEffect(() => {
    if (!isSSR) {
      document?.body?.classList?.add('productPage');
      TagManager.productDetail(product);
    }
    return () => {
      document?.body?.classList?.remove('productPage');
    };
  }, [isSSR]);

  const productSchema = useMemo(() => <ProductSchema product={product} node={node} />, [product, node]);

  useEffect(() => {
    setPageView('productpage');
    if (hasNostoJs && !isSSR && typeof product?.id === 'string') {
      getProductPageRecommendations(product.id, [data.recentlyViewedPlacement, data.relatedPlacement]);
    }
  }, [hasNostoJs, isSSR, product?.id]);

  if (!product) {
    return <PrimaryLoader mode="globalInner" />;
  }

  return (
    <>
      {productSchema}
      <YotpoWidget />
      {isGiftPage ? (
        <GiftCartPage
          title={product?.name || ''}
          description={product?.description || ''}
          product={product}
          breadcrumbs={fullBreadcrumbs}
          handleAddToCart={(props) => {
            return addCustomLineItemToCart(props);
          }}
        />
      ) : (
        <ProductPage
          isSSR={isSSR}
          data={data}
          breadcrumbs={fullBreadcrumbs}
          context={context}
          product={product}
          hasAvailableSizeGuide={!!hasAvailableSizeGuide && hasGuideById}
          handleAddToCart={(selectedVariant, count, channel) => {
            return addToCart(selectedVariant, count, { channel }).then((data) => {
              if (!data) return;
              nostoUpdateCart(data);
              TagManager.addToCart(product, selectedVariant, count, data.cart);
            });
          }}
          handleChangeVariant={(variant) => {
            TagManager.productDetail(product, variant);
          }}
          onOpenChat={openChat}
          onCloseChat={closeChat}
          nostoProductClick={nostoProductClick}
        />
      )}
    </>
  );
};

ProductPageTastic.propTypes = {
  product: PropTypes.object,
  wishlist: PropTypes.object,
};

export default tastify({
  translate: true,
  connect: {
    node: true,
    context: true,
    tastic: true,
    route: true,
    cart: true,
    wishlist: true,
    urlHandler: true,
    isServerSideRendering: true,
  },
})(
  compose(
    withZendesk,
    connect(productConnector),
    connect(urlHandlerConnector),
    connect((globalState) => {
      // @ts-ignore
      const streamParameters = globalState.app.route.parameters.s || {};
      return {
        // @ts-ignore
        route: globalState.app.route,
        streamParameters,
      };
    }),
  )(ProductPageTastic),
);
